import { useCallback } from 'react';
import {
  type IAsyncService,
  type IAsyncServiceParameter,
} from '../../../../expressions/repository/async/context/IAsyncService.ts';
import { type IAsyncServiceOutputsDiff } from './compareAsyncServiceOutputs.ts';

export interface IHandleStaleOutputsUpdateOptions {
  selected: IAsyncService | undefined;
  diff: IAsyncServiceOutputsDiff | null;
  currentLabelFields: string[] | undefined;
  currentValueField: string | null;
  setOutputsField: (value: IAsyncServiceParameter[]) => void;
  setLabelFields: (value: string[]) => void;
  setValueField: (value: string | null) => void;
  onAfterUpdate?: (diff: IAsyncServiceOutputsDiff) => void;
}

export function useHandleStaleOutputsUpdate(
  options: IHandleStaleOutputsUpdateOptions,
): () => void {
  const {
    selected,
    diff,
    currentLabelFields,
    currentValueField,
    setOutputsField,
    setLabelFields,
    setValueField,
    onAfterUpdate,
  } = options;

  return useCallback(() => {
    if (!(selected && diff)) return;

    setOutputsField(selected.outputs);

    const removedNames = new Set(diff.removed.map(p => p.name));
    if (currentLabelFields) {
      const filtered = currentLabelFields.filter(f => !removedNames.has(f));
      if (filtered.length !== currentLabelFields.length) {
        setLabelFields(filtered);
      }
    }
    if (currentValueField && removedNames.has(currentValueField)) {
      setValueField(null);
    }

    onAfterUpdate?.(diff);
  }, [
    selected,
    diff,
    currentLabelFields,
    currentValueField,
    setOutputsField,
    setLabelFields,
    setValueField,
    onAfterUpdate,
  ]);
}
